import React, { useEffect, useRef, useState } from "react";
import { getCalApi } from "@calcom/embed-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const LINES = [
  ["Let's", "Build"],
  ["It", "Right."],
];

export default function ContactMe() {
  // one ref per word across both lines
  const wordRefs = useRef([]);
  const headingWrapRef = useRef(null);
  const subRef = useRef(null);
  const btnRef = useRef(null);
  const btnWrapRef = useRef(null);
  const [btnHover, setBtnHover] = useState(false);
  const [mailHover, setMailHover] = useState(false);

  /* cal.com */
  useEffect(() => {
    (async () => {
      const cal = await getCalApi({ namespace: "project-intro-call" });
      cal("ui", {
        theme: "dark", styles: { branding: { brandColor: "#0A0A08" } },
        hideEventTypeDetails: false, layout: "month_view", hideBranding: true
      });
    })();
  }, []);

  /* scroll animations */
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    const words = wordRefs.current.filter(Boolean);
    if (!words.length) return;

    // set initial state outside gsap.context so it applies immediately
    gsap.set(words, { y: "110%" });

    const ctx = gsap.context(() => {
      gsap.to(words, {
        y: "0%",
        duration: 1.15,
        ease: "power4.out",
        stagger: 0.09,
        scrollTrigger: {
          trigger: headingWrapRef.current,
          start: isMobile ? "top 92%" : "top 78%",
          toggleActions: "play none none none",
        },
        onComplete: () => words.forEach(el => { el.style.willChange = "auto"; }),
      });

      gsap.fromTo([subRef.current, btnRef.current],
        { opacity: 0, y: 28 },
        {
          opacity: 1, y: 0, duration: 0.85, stagger: 0.15, ease: "power3.out",
          scrollTrigger: {
            trigger: headingWrapRef.current,
            start: isMobile ? "top 92%" : "top 78%",
            toggleActions: "play none none none",
          }
        }
      );
    });

    return () => ctx.revert();
  }, []);

  /* magnetic button — only active while hovering */
  useEffect(() => {
    const wrap = btnWrapRef.current;
    const btn = btnRef.current;
    if (!wrap || !btn) return;

    let active = false;
    let pending = false;
    let mx = 0, my = 0;

    const process = () => {
      pending = false;
      if (!active) return;
      const rect = wrap.getBoundingClientRect();
      const dx = mx - (rect.left + rect.width / 2);
      const dy = my - (rect.top + rect.height / 2);
      gsap.to(btn, { x: dx * 0.38, y: dy * 0.38, duration: 0.3, ease: "power2.out" });
    };
    const onMove = (e) => {
      mx = e.clientX; my = e.clientY;
      if (active && !pending) { pending = true; requestAnimationFrame(process); }
    };
    const onEnter = () => { active = true; };
    const onLeave = () => {
      active = false;
      gsap.to(btn, { x: 0, y: 0, duration: 0.5, ease: "elastic.out(1, 0.5)" });
    };

    wrap.addEventListener("mouseenter", onEnter);
    wrap.addEventListener("mouseleave", onLeave);
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      wrap.removeEventListener("mouseenter", onEnter);
      wrap.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("mousemove", onMove);
    };
  }, []);

  let wordIndex = 0;

  return (
    <section
      id="contact"
      style={{ background: "#0D0D0B", color: "#F5F4F0" }}
      className="w-full px-6 md:px-12 lg:px-20 py-24 md:py-36 flex flex-col items-center justify-center relative overflow-hidden"
    >
      {/* gradient blobs */}
      <div aria-hidden style={{
        position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none",
        background: [
          "radial-gradient(ellipse 70% 55% at 15% 100%, rgba(88,56,230,0.11) 0%, transparent 65%)",
          "radial-gradient(ellipse 55% 50% at 85% 0%, rgba(160,72,255,0.08) 0%, transparent 60%)",
          "radial-gradient(ellipse 45% 40% at 50% 50%, rgba(30,180,140,0.04) 0%, transparent 70%)",
        ].join(", "),
      }} />

      {/* SVG noise texture */}
      <svg aria-hidden style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 1, pointerEvents: "none", opacity: 0.055 }}>
        <filter id="contact-noise">
          <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" />
          <feColorMatrix type="saturate" values="0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#contact-noise)" />
      </svg>

      {/* top fade */}
      <div aria-hidden style={{
        position: "absolute", top: 0, left: 0, right: 0, height: "180px",
        zIndex: 2, pointerEvents: "none",
        background: "linear-gradient(to bottom, #0A0A08 0%, transparent 100%)",
      }} />

      {/* bottom fade */}
      <div aria-hidden style={{
        position: "absolute", bottom: 0, left: 0, right: 0, height: "180px",
        zIndex: 2, pointerEvents: "none",
        background: "linear-gradient(to top, #0A0A08 0%, transparent 100%)",
      }} />

      {/* sub label */}
      <span
        ref={subRef}
        className="font-Montreal-Mono text-xs tracking-[0.28em] uppercase mb-10 opacity-0"
        style={{ color: "rgba(245,244,240,0.3)", position: "relative", zIndex: 3 }}
      >
        Got a problem worth solving?
      </span>

      {/* headline */}
      <div
        ref={headingWrapRef}
        className="text-center mb-14 md:mb-20"
        style={{ position: "relative", zIndex: 3 }}
      >
        {LINES.map((words, li) => (
          <div key={li} className="flex justify-center gap-[0.28em]">
            {words.map((word) => {
              const idx = wordIndex++;
              return (
                <div key={word} style={{ overflow: "hidden", display: "inline-block" }}>
                  <span
                    ref={el => { wordRefs.current[idx] = el; }}
                    className="font-Montreal-Bold font-black uppercase leading-[0.88] inline-block"
                    style={{
                      fontSize: "clamp(2.8rem, 9vw, 10vw)",
                      letterSpacing: "-0.03em",
                    }}
                  >
                    {word}
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* magnetic CTA */}
      <div ref={btnWrapRef} className="relative inline-block mb-20" style={{ zIndex: 5 }}>
        <button
          ref={btnRef}
          onMouseEnter={() => setBtnHover(true)}
          onMouseLeave={() => setBtnHover(false)}
          data-cal-namespace="project-intro-call"
          data-cal-link="aneesazhar/project-intro-call"
          data-cal-config='{"layout":"month_view"}'
          className="rounded-full font-Montreal-Bold font-bold opacity-0"
          style={{
            padding: "18px 52px",
            fontSize: "clamp(1rem,1.4vw,1.25rem)",
            background: btnHover ? "#F5F4F0" : "rgba(245,244,240,0.08)",
            color: btnHover ? "#0A0A08" : "#F5F4F0",
            border: "1.5px solid rgba(245,244,240,0.2)",
            letterSpacing: btnHover ? "0.06em" : "0em",
            transition: "background 0.4s ease, color 0.4s ease, letter-spacing 0.35s ease",
          }}
        >
          Book a Call ↗
        </button>
      </div>

      {/* bottom-right email */}
      <div className="absolute bottom-8 right-8 md:right-14 flex flex-col items-end gap-1" style={{ zIndex: 3 }}>
        <span className="font-Montreal-Mono text-[10px] tracking-widest uppercase"
          style={{ color: "rgba(245,244,240,0.22)" }}>
          For enquiries
        </span>
        <a
          href="mailto:aneesazhar1993@gmail.com"
          className="font-Montreal-Mono text-sm lusion-link"
          onMouseEnter={() => setMailHover(true)}
          onMouseLeave={() => setMailHover(false)}
          style={{ color: "#F5F4F0", opacity: mailHover ? 1 : 0.4, transition: "opacity 0.25s" }}
        >
          Drop me a message
        </a>
      </div>
    </section>
  );
}
